/**
 * ModuleNavSidebar
 *
 * Reusable left-hand vertical navigation sidebar for module-level navigation.
 * Used across Data, Cost, Price, Negotiate, and Publish modules.
 *
 * **Two Usage Patterns:**
 *
 * 1. **Entity Navigation** (DataPage):
 *    - Switches between different entities (offerings, vendors, customers)
 *    - Each entity has different API endpoint
 *    - Uses `sections` prop for grouped display
 *
 * 2. **Type Filtering** (CostPage, PricePage):
 *    - Filters same entity by type (REPLACEMENT_COST, MSRP, MAP)
 *    - Same API endpoint with filter applied
 *    - Uses flat `items` with optional `header`
 *
 * **Features:**
 * - Expand/collapse with localStorage persistence
 * - Keyboard shortcuts ([ to toggle, 1-9 to jump)
 * - Feature tier filtering via featureTierService
 * - Section grouping support
 * - Mobile responsive (becomes horizontal menu on small screens)
 *
 * @example Entity Navigation (with sections)
 * ```tsx
 * <ModuleNavSidebar
 *   items={[
 *     { key: 'offerings', label: 'Offerings', icon: <ShoppingOutlined />, section: 'master-data' },
 *     { key: 'vendors', label: 'Vendors', icon: <ShopOutlined />, section: 'master-data' },
 *   ]}
 *   sections={[
 *     { key: 'master-data', label: 'Master Data', order: 1 },
 *   ]}
 *   selectedKey={selectedEntity}
 *   onSelect={setSelectedEntity}
 *   storageKey="dataPage.sidebar"
 * />
 * ```
 *
 * @example Type Filtering (flat list)
 * ```tsx
 * <ModuleNavSidebar
 *   items={[
 *     { key: 'REPLACEMENT_COST', label: 'Replacement Cost', icon: <DollarOutlined /> },
 *   ]}
 *   header="Cost Types"
 *   selectedKey={selectedType}
 *   onSelect={setSelectedType}
 *   storageKey="costPage.sidebar"
 * />
 * ```
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Menu, Tooltip, Grid } from 'antd'
import {
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'

import { useCollapsedState } from './hooks/useCollapsedState'
import { featureTierService } from '@/services/featureTierService'
import { useAuth } from '@/hooks/useAuth'
import type { ModuleNavSidebarProps, NavItem, NavSection } from '@/types/navigation.types'

import './ModuleNavSidebar.css'

const { useBreakpoint } = Grid

/**
 * Group items by section, returning sorted sections with their items.
 * Items without a section go into an implicit "other" group at the end.
 */
function groupItemsBySection(
  items: NavItem[],
  sections: NavSection[]
): { section: NavSection | null; items: NavItem[] }[] {
  const sectionMap = new Map<string, NavItem[]>()
  const unsectioned: NavItem[] = []

  // Group items by their section key
  for (const item of items) {
    if (item.section) {
      const existing = sectionMap.get(item.section) || []
      existing.push(item)
      sectionMap.set(item.section, existing)
    } else {
      unsectioned.push(item)
    }
  }

  // Build result array in section order
  const sortedSections = [...sections].sort((a, b) => a.order - b.order)
  const result: { section: NavSection | null; items: NavItem[] }[] = []

  for (const section of sortedSections) {
    const sectionItems = sectionMap.get(section.key) || []
    if (sectionItems.length > 0) {
      result.push({ section, items: sectionItems })
    }
  }

  // Add unsectioned items at end (no header)
  if (unsectioned.length > 0) {
    result.push({ section: null, items: unsectioned })
  }

  return result
}

export function ModuleNavSidebar({
  items,
  selectedKey,
  onSelect,
  sections,
  header,
  defaultCollapsed = false,
  storageKey,
  expandedWidth = 220,
  collapsedWidth = 64,
}: ModuleNavSidebarProps) {
  const { user } = useAuth()
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const { collapsed, setCollapsed, toggleCollapsed } = useCollapsedState({
    storageKey,
    defaultCollapsed: isMobile ? true : defaultCollapsed,
  })

  // Controlled tooltip state to prevent tooltip sticking after click
  const [tooltipOpen, setTooltipOpen] = useState(false)

  // Toggle handler that closes tooltip immediately
  const handleToggleClick = useCallback(() => {
    setTooltipOpen(false)
    toggleCollapsed()
  }, [toggleCollapsed])

  // Filter items by user's feature tier access
  const visibleItems = useMemo(() => {
    return items.filter(
      (item) => !item.tier || featureTierService.hasFeatureAccess(user, item.tier)
    )
  }, [items, user])

  // Group items by section (if sections provided)
  const groupedItems = useMemo(() => {
    if (sections && sections.length > 0) {
      return groupItemsBySection(visibleItems, sections)
    }
    return [{ section: null, items: visibleItems }]
  }, [visibleItems, sections])

  // Build menu items for Ant Design Menu (with section headers as group type)
  // IMPORTANT: Always maintain DOM structure (groups) to prevent icon jitter on collapse
  const menuItems: MenuProps['items'] = useMemo(() => {
    const result: MenuProps['items'] = []

    for (const group of groupedItems) {
      // Always use group structure if sections exist to prevent jitter
      if (group.section) {
        result.push({
          type: 'group',
          // Hide label text when collapsed, but keep group structure
          label: collapsed ? '' : group.section.label.toUpperCase(),
          key: `section-${group.section.key}`,
          children: group.items.map((item) => ({
            key: item.key,
            icon: item.icon,
            label: collapsed ? null : item.label,
            title: collapsed ? item.label : item.tooltip,
          })),
        })
      } else {
        // Flat items (no section)
        for (const item of group.items) {
          result.push({
            key: item.key,
            icon: item.icon,
            label: collapsed ? null : item.label,
            title: collapsed ? item.label : item.tooltip,
          })
        }
      }
    }

    return result
  }, [groupedItems, collapsed])

  // Handle selection
  const handleSelect = useCallback(
    ({ key }: { key: string }) => {
      onSelect(key)
      // Auto-collapse on mobile after selection
      if (isMobile && !collapsed) {
        setCollapsed(true)
      }
    },
    [onSelect, isMobile, collapsed, setCollapsed]
  )

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger in input fields
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // [ to toggle collapse
      if (e.key === '[' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        toggleCollapsed()
        return
      }

      // 1-9 to jump to entity by position
      const num = parseInt(e.key, 10)
      if (num >= 1 && num <= 9 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const targetItem = visibleItems[num - 1]
        if (targetItem) {
          e.preventDefault()
          onSelect(targetItem.key)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [toggleCollapsed, visibleItems, onSelect])

  // Determine aria-label based on context
  const ariaLabel = sections && sections.length > 0
    ? 'Module navigation'
    : `${header || 'Module'} navigation`

  // Mobile: render as horizontal menu bar
  if (isMobile) {
    return (
      <div className="module-nav-mobile">
        <Menu
          mode="horizontal"
          selectedKeys={[selectedKey]}
          onClick={handleSelect}
          items={visibleItems.map((item) => ({
            key: item.key,
            icon: item.icon,
            label: item.label,
          }))}
          className="module-nav-mobile-menu"
        />
      </div>
    )
  }

  // Desktop: render as collapsible sidebar
  return (
    <nav
      className={`module-nav-sidebar ${collapsed ? 'collapsed' : 'expanded'}`}
      style={{ width: collapsed ? collapsedWidth : expandedWidth }}
      role="navigation"
      aria-label={ariaLabel}
    >
      {/* Header (only shown for flat lists without sections) */}
      {!sections?.length && header && !collapsed && (
        <div className="module-nav-header">
          <span className="module-nav-title">{header}</span>
        </div>
      )}

      {/* Navigation menu */}
      <div className="module-nav-content">
        <Menu
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={[selectedKey]}
          onClick={handleSelect}
          items={menuItems}
          className="module-nav-menu"
        />
      </div>

      {/* Footer with collapse toggle - pinned to bottom */}
      <div className="module-nav-footer">
        <Tooltip
          title={collapsed ? 'Expand sidebar [' : 'Collapse sidebar ['}
          open={tooltipOpen}
          onOpenChange={setTooltipOpen}
        >
          <button
            type="button"
            className="module-nav-toggle"
            onClick={handleToggleClick}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <RightOutlined /> : <LeftOutlined />}
            {!collapsed && <span className="module-nav-toggle-label">Collapse</span>}
          </button>
        </Tooltip>
      </div>
    </nav>
  )
}

export default ModuleNavSidebar
